// Lightweight analytics — loaded lazily from env vars.
// If an ID is missing, nothing is injected for that provider and helpers no-op.
//
// Providers wired:
//   REACT_APP_GA4_ID          — Google Analytics 4 (also covers Google Ads / Consent Mode)
//   REACT_APP_META_PIXEL_ID   — Meta / Facebook Pixel
//   REACT_APP_BING_UET_ID     — Microsoft/Bing UET (also serves Yahoo Search since 2015)
//   REACT_APP_CLARITY_ID      — Microsoft Clarity (heatmaps + session replay)

const GA_ID     = process.env.REACT_APP_GA4_ID;
const META_ID   = process.env.REACT_APP_META_PIXEL_ID;
const BING_ID   = process.env.REACT_APP_BING_UET_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

let loaded = false;

function injectScript(src, attrs = {}) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
  return s;
}

export function initAnalytics() {
  if (loaded) return;
  loaded = true;

  // ---- Google Analytics 4 -----------------------------------------------------
  if (GA_ID) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", GA_ID, { anonymize_ip: true });
  }

  // ---- Meta Pixel -------------------------------------------------------------
  if (META_ID) {
    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      t = b.createElement(e); t.async = true;
      t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", META_ID);
    window.fbq("track", "PageView");
  }

  // ---- Microsoft/Bing UET (also serves Yahoo Search) --------------------------
  if (BING_ID) {
    (function (w, d, t, r, u) {
      var f, n, i;
      w[u] = w[u] || [], f = function () {
        var o = { ti: BING_ID, enableAutoSpaTracking: true };
        // eslint-disable-next-line no-undef
        o.q = w[u], w[u] = new w.UET(o), w[u].push("pageLoad");
      };
      n = d.createElement(t), n.src = r, n.async = 1, n.onload = n.onreadystatechange = function () {
        var s = this.readyState;
        s && s !== "loaded" && s !== "complete" || (f(), n.onload = n.onreadystatechange = null);
      };
      i = d.getElementsByTagName(t)[0], i.parentNode.insertBefore(n, i);
    })(window, document, "script", "https://bat.bing.com/bat.js", "uetq");
  }

  // ---- Microsoft Clarity ------------------------------------------------------
  if (CLARITY_ID) {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_ID);
  }
}

export function trackPageView(path) {
  if (GA_ID && window.gtag) window.gtag("event", "page_view", { page_path: path });
  if (META_ID && window.fbq) window.fbq("track", "PageView");
  if (BING_ID && window.uetq) window.uetq.push("event", "page_view", { page_path: path });
  if (CLARITY_ID && window.clarity) window.clarity("set", "page", path);
}

export function trackEvent(name, params = {}) {
  if (GA_ID && window.gtag) window.gtag("event", name, params);
  if (META_ID && window.fbq) window.fbq("trackCustom", name, params);
  if (BING_ID && window.uetq) window.uetq.push("event", name, params);
  if (CLARITY_ID && window.clarity) window.clarity("event", name);
}

export const analyticsEnabled = () => Boolean(GA_ID || META_ID || BING_ID || CLARITY_ID);
